import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { Badge, Button, Card, EmptyState, PageHeader, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { formatDate } from './dates';
import { BillingTenantSettings, TENANT, TenantBillingView, formatAllowance, formatPounds, formatUsage, invoiceStatusLabel, invoiceStatusTone, keys, metricLabel, subStatusLabel, subStatusTone } from './api';
import { Money } from './parts';
import { SmsUsageLine } from '../notifications/SmsUsageLine';

type View = TenantBillingView;

function UsageBars({ rows }: { rows: View['usageThisPeriod'] }) {
  if (rows.length === 0) return <p className="text-sm text-slate-600">{t('billingUi.tenant.noUsage')}</p>;
  return (
    <ul className="space-y-4">
      {rows.map((r) => {
        const pct = r.allowance ? Math.min(100, Math.round((r.used / r.allowance) * 100)) : 0;
        const over = r.allowance !== null && r.allowance > 0 && r.used > r.allowance;
        const text = r.allowance === null ? formatUsage(r.metric, r.used) : t('billingUi.tenant.usedOf', { used: formatUsage(r.metric, r.used), allowance: formatAllowance(r.metric, r.allowance) });
        return (
          <li key={r.metric}>
            <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2 text-sm">
              <span className="font-medium">{metricLabel(r.metric)}</span>
              <span>
                {text}
                {over ? <span className="ml-2 font-medium text-amber-900">{t('billingUi.tenant.over')}</span> : null}
              </span>
            </div>
            {r.allowance !== null ? (
              <div
                role="progressbar"
                aria-label={metricLabel(r.metric)}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={pct}
                aria-valuetext={text}
                className="h-2 w-full overflow-hidden rounded-full bg-slate-200"
              >
                <div className={`h-full ${over ? 'bg-amber-600' : 'bg-brand'}`} style={{ width: `${pct}%` }} />
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function ContactCard({ view, canManage }: { view: View; canManage: boolean }) {
  const c = view.contact;
  const [form, setForm] = useState({ contactName: c.name, contactEmail: c.email, organisation: c.organisation, address: c.address, purchaseOrder: c.purchaseOrder });
  const qc = useQueryClient();
  const toast = useToast();
  const emailOk = BillingTenantSettings.shape.contactEmail.safeParse(form.contactEmail).success;
  const dirty = form.contactName !== c.name || form.contactEmail !== c.email || form.organisation !== c.organisation || form.address !== c.address || form.purchaseOrder !== c.purchaseOrder;
  const save = useMutation({
    mutationFn: () => api.patch(`${TENANT}/contact`, { body: form, schema: TenantBillingView }),
    onSuccess: async (r) => {
      toast(t('billingUi.tenant.contactSaved'));
      qc.setQueryData(keys.tenant, r);
      await qc.invalidateQueries({ queryKey: keys.tenant });
    },
  });
  const errs = fieldErrors(save.error);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm({ ...form, [k]: e.target.value });
  const ro = !canManage;
  return (
    <Card
      title={t('billingUi.tenant.contactTitle')}
      description={t('billingUi.tenant.contactHelp')}
      footer={
        canManage ? (
          <Button variant="primary" disabled={!dirty || !emailOk || save.isPending} onClick={() => save.mutate()}>
            {t('common.save')}
          </Button>
        ) : undefined
      }
    >
      <ErrorLine error={save.error && Object.keys(errs).length === 0 ? save.error : null} />
      <form className="grid gap-3 sm:grid-cols-2" onSubmit={(e) => e.preventDefault()}>
        <TextField label={t('billingUi.tenant.contactName')} value={form.contactName} readOnly={ro} maxLength={120} error={errs.contactName} onChange={set('contactName')} />
        <TextField
          label={t('billingUi.tenant.contactEmail')}
          hint={t('billingUi.tenant.contactEmailHint')}
          type="email"
          value={form.contactEmail}
          readOnly={ro}
          error={!emailOk ? t('billingUi.tenant.emailInvalid') : errs.contactEmail}
          onChange={set('contactEmail')}
        />
        <TextField label={t('billingUi.tenant.organisation')} value={form.organisation} readOnly={ro} maxLength={200} onChange={set('organisation')} />
        <TextField label={t('billingUi.tenant.purchaseOrder')} hint={t('billingUi.tenant.purchaseOrderHint')} value={form.purchaseOrder} readOnly={ro} maxLength={60} onChange={set('purchaseOrder')} />
        <TextField className="sm:col-span-2" label={t('billingUi.tenant.address')} value={form.address} readOnly={ro} maxLength={500} onChange={set('address')} />
      </form>
    </Card>
  );
}

/** Tenant: current plan, usage against allowance, balance, invoices with PDFs and the billing contact. */
export function TenantBillingPage() {
  const canManage = useCan('billing.manage');
  const q = useQuery({ queryKey: keys.tenant, queryFn: () => api.get(TENANT, { schema: TenantBillingView }) });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  const v = q.data;
  const s = v.subscription;
  const due = v.invoices.filter((i) => i.kind === 'invoice' && i.status === 'issued').reduce((sum, i) => sum + i.balance, 0);
  return (
    <>
      <PageHeader title={t('billingUi.tenant.title')} description={t('billingUi.tenant.description')} />
      <SmsUsageLine />
      <div className="mb-5 grid gap-5 lg:grid-cols-2">
        <Card title={t('billingUi.tenant.planTitle')}>
          {s && v.plan ? (
            <dl className="space-y-2 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <dt className="sr-only">{t('billingUi.col.plan')}</dt>
                <dd className="text-lg font-semibold">{v.plan.name}</dd>
                <Badge tone={subStatusTone(s.status)}>{subStatusLabel(s.status)}</Badge>
              </div>
              <div>
                {s.period === 'monthly'
                  ? t('billingUi.tenant.priceMonthly', { price: formatPounds(v.plan.monthlyPrice) })
                  : t('billingUi.tenant.priceAnnual', { price: formatPounds(v.plan.annualPrice) })}
              </div>
              <div>{t('billingUi.sub.range', { from: formatDate(s.currentPeriodStart), to: formatDate(s.currentPeriodEnd) })}</div>
              {s.trialEndsOn ? <div>{t('billingUi.tenant.trialEnds', { date: formatDate(s.trialEndsOn) })}</div> : null}
              {s.nextInvoiceOn ? <div>{t('billingUi.tenant.nextInvoice', { date: formatDate(s.nextInvoiceOn) })}</div> : null}
              {s.discountPercent > 0 ? <div>{t('billingUi.tenant.discount', { percent: s.discountPercent })}</div> : null}
            </dl>
          ) : (
            <p className="text-sm text-slate-600">{t('billingUi.tenant.noPlan')}</p>
          )}
        </Card>
        <Card title={t('billingUi.tenant.balanceTitle')}>
          <p className="text-3xl font-bold tabular-nums" aria-label={t('billingUi.tenant.balanceLabel', { amount: formatPounds(due) })}>
            {formatPounds(due)}
          </p>
          <p className="mt-1 text-sm text-slate-600">{due > 0 ? t('billingUi.tenant.balanceDue') : t('billingUi.tenant.balanceClear')}</p>
          <p className="mt-3 text-sm">
            {v.paymentMethodUrl ? (
              <a className="font-medium text-brand hover:underline" href={v.paymentMethodUrl} target="_blank" rel="noopener noreferrer">
                {t('billingUi.tenant.updatePayment')}
              </a>
            ) : (
              t('billingUi.tenant.bankTransfer')
            )}
          </p>
        </Card>
      </div>
      <div className="mb-5">
        <Card title={t('billingUi.tenant.usageTitle')} description={t('billingUi.tenant.usageHelp')}>
          <UsageBars rows={v.usageThisPeriod} />
        </Card>
      </div>
      <h2 className="mb-2 text-base font-semibold text-slate-900">{t('billingUi.tenant.invoicesTitle')}</h2>
      <div className="mb-5">
        {v.invoices.length === 0 ? (
          <Card>
            <EmptyState message={t('billingUi.tenant.noInvoices')} />
          </Card>
        ) : (
          <TableWrap caption={t('billingUi.tenant.invoicesTable')}>
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <Th>{t('billingUi.col.number')}</Th>
                <Th>{t('billingUi.invoices.kind')}</Th>
                <Th>{t('billingUi.col.issued')}</Th>
                <Th>{t('billingUi.col.due')}</Th>
                <Th>{t('billingUi.col.total')}</Th>
                <Th>{t('billingUi.col.balance')}</Th>
                <Th>{t('common.status')}</Th>
                <Th>{t('billingUi.tenant.pdf')}</Th>
              </tr>
            </thead>
            <tbody>
              {v.invoices.map((i) => (
                <tr key={i.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-medium">{i.number}</td>
                  <td className="px-4 py-3">{t(`billingUi.kind.${i.kind}`)}</td>
                  <td className="px-4 py-3">{formatDate(i.issuedOn)}</td>
                  <td className="px-4 py-3">{formatDate(i.dueOn)}</td>
                  <td className="px-4 py-3"><Money pence={i.total} /></td>
                  <td className="px-4 py-3"><Money pence={i.balance} /></td>
                  <td className="px-4 py-3"><Badge tone={invoiceStatusTone(i.status)}>{invoiceStatusLabel(i.status)}</Badge></td>
                  <td className="px-4 py-3">
                    {i.pdfAvailable ? (
                      <a className="font-medium text-brand hover:underline" href={`/api/v1${TENANT}/invoices/${i.id}/pdf`} target="_blank" rel="noopener noreferrer" aria-label={t('billingUi.tenant.downloadPdf', { number: i.number ?? '' })}>
                        {t('billingUi.tenant.pdf')}
                      </a>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </div>
      <ContactCard key={JSON.stringify(v.contact)} view={v} canManage={canManage} />
    </>
  );
}
